import React, { useState } from "react";
import { ArrowLeft, CreditCard, User, Mail, Phone, Music } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Checkout = ({ items, onBack, onComplete, onRemoveFromCart }) => {
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
  });
  const [loading, setLoading] = useState(false);

  const getTotalPrice = () => {
    return items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  };

  const getTotalQuantity = () => {
    return items.reduce((sum, item) => sum + item.quantity, 0);
  };

  const getServiceFee = () => {
    return items.reduce((sum, item) => {
      if (item.price <= 0) return sum;
      const fee = item.event_service_fee !== undefined ? item.event_service_fee : 0.99;
      return sum + fee * item.quantity;
    }, 0);
  };

  const getGrandTotal = () => {
    return getTotalPrice() + getServiceFee();
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.customer_name || !formData.customer_email || !formData.customer_phone) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.customer_email)) {
      toast.error("Veuillez entrer une adresse email valide");
      return;
    }

    setLoading(true);

    try {
      const orderData = {
        items: items,
        total: getGrandTotal(),
        service_fee: getServiceFee(),
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
      };

      console.log("Creating order...", orderData);

      // Step 1: Create the order
      const orderResponse = await axios.post(`${API}/orders`, orderData);
      console.log("Order created:", orderResponse.data);

      if (orderResponse.data && orderResponse.data.id) {
        const orderId = orderResponse.data.id;
        
        // FREE ORDER: skip Stripe, confirm directly
        if (getGrandTotal() === 0) {
          console.log("Free order - confirming directly...");
          await axios.post(`${API}/orders/${orderId}/confirm-free`);
          toast.success("Billet confirmé !");
          onComplete(orderId);
          return;
        }
        
        // PAID ORDER: Create Stripe checkout session
        console.log("Creating Stripe checkout session...");
        const checkoutResponse = await axios.post(`${API}/payments/checkout`, {
          order_id: orderId,
          origin_url: window.location.origin
        });
        
        console.log("Stripe response:", checkoutResponse.data);
        
        if (checkoutResponse.data && checkoutResponse.data.checkout_url) {
          // Redirect to Stripe - use replace to prevent back button issues
          toast.success("Redirection vers Stripe...");
          window.location.replace(checkoutResponse.data.checkout_url);
          return; // Important: stop execution here
        } else {
          // Fallback if no checkout URL
          toast.success("Commande confirmée !");
          onComplete(orderId);
        }
      } else {
        throw new Error("La création de commande a échoué");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      const errorMsg = error.response?.data?.detail || error.message || "Une erreur est survenue";
      toast.error(errorMsg);
      setLoading(false);
    }
    // Note: Don't setLoading(false) in finally - we want to keep loading during redirect
  };

  return (
    <div className="min-h-screen bg-background/40">
      {/* Header */}
      <header className="border-b border-primary/10 sticky top-0 bg-background/95 backdrop-blur-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button
              data-testid="back-to-cart-btn"
              variant="ghost"
              onClick={onBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour
            </Button>
            
            {/* Mini Event Display */}
            {items.length > 0 && (
              <div className="hidden sm:flex items-center gap-3 px-4 py-2 bg-primary/5 rounded-lg border border-primary/10">
                <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0">
                  {items[0].event_image ? (
                    <img 
                      src={items[0].event_image} 
                      alt={items[0].event_title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-primary/20 flex items-center justify-center">
                      <Music className="w-5 h-5 text-primary" />
                    </div>
                  )}
                </div>
                <div className="text-left">
                  <div className="text-xs font-semibold truncate max-w-[200px]">{items[0].event_title}</div>
                  <div className="text-xs text-muted-foreground">{getGrandTotal().toFixed(2)}€</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl sm:text-4xl text-street tracking-wide mb-8">
          Finaliser votre commande
        </h1>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Checkout Form */}
          <div className="space-y-6">
            {/* Event Card - Always visible */}
            {items.length > 0 && (
              <div className="glass-card rounded-lg p-4 border-l-4 border-primary mb-6">
                <div className="flex items-start gap-4">
                  <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                    {items[0].event_image ? (
                      <img 
                        src={items[0].event_image} 
                        alt={items[0].event_title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-primary/10 flex items-center justify-center">
                        <Music className="w-8 h-8 text-primary" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-lg mb-1 truncate">{items[0].event_title}</h3>
                    <p className="text-sm text-muted-foreground">{items[0].event_artist}</p>
                    <p className="text-xs text-muted-foreground mt-1">{items[0].event_date}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="glass-card rounded-lg p-6">
              <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Informations personnelles
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="customer_name">Nom complet *</Label>
                  <Input
                    data-testid="customer-name-input"
                    id="customer_name"
                    name="customer_name"
                    type="text"
                    value={formData.customer_name}
                    onChange={handleInputChange}
                    placeholder="Jean Dupont"
                    required
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="customer_email">Email *</Label>
                  <Input
                    data-testid="customer-email-input"
                    id="customer_email"
                    name="customer_email"
                    type="email"
                    value={formData.customer_email}
                    onChange={handleInputChange}
                    placeholder="jean.dupont@email.com"
                    required
                    className="mt-2"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Vos billets seront envoyés à cette adresse
                  </p>
                </div>

                <div>
                  <Label htmlFor="customer_phone">Téléphone *</Label>
                  <Input
                    data-testid="customer-phone-input"
                    id="customer_phone"
                    name="customer_phone"
                    type="tel"
                    value={formData.customer_phone}
                    onChange={handleInputChange}
                    placeholder="+33 6 12 34 56 78"
                    required
                    className="mt-2"
                  />
                </div>

                <div className="pt-6 border-t border-border">
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-primary" />
                    Paiement
                  </h3>
                  <div className="bg-muted/30 rounded-lg p-4 mb-4">
                    <p className="text-sm text-muted-foreground">
                      💳 Paiement sécurisé par carte bancaire
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      Vos informations sont protégées et cryptées
                    </p>
                  </div>
                </div>

                <Button
                  data-testid="confirm-order-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground py-6 text-lg"
                >
                  {loading ? "Traitement..." : getGrandTotal() === 0 ? "Confirmer mon billet gratuit" : `Confirmer et payer ${getGrandTotal().toFixed(2)}€`}
                </Button>
              </form>
            </div>
          </div>

          {/* Order Summary */}
          <div>
            <div className="glass-card rounded-lg p-6 sticky top-24">
              <h2 className="text-xl font-bold mb-6">Récapitulatif</h2>

              <div className="space-y-4 mb-6">
                {items.map((item, index) => (
                  <div
                    key={index}
                    data-testid={`summary-item-${index}`}
                    className="pb-4 border-b border-border last:border-0"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <h4 className="font-semibold text-sm">{item.event_title}</h4>
                        <p className="text-xs text-muted-foreground">{item.event_date}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {item.ticket_type} × {item.quantity}
                        </p>
                      </div>
                      <span className="font-bold text-primary">
                        {(item.price * item.quantity).toFixed(2)}€
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-2 pt-4 border-t border-border">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Sous-total</span>
                  <span>{getTotalPrice().toFixed(2)}€</span>
                </div>
                {getServiceFee() > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Frais de service</span>
                    <span>{getServiceFee().toFixed(2)}€</span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-4 border-t border-border">
                  <span className="text-lg font-semibold">Total</span>
                  <span className="text-2xl font-bold text-primary" data-testid="checkout-total">
                    {getGrandTotal().toFixed(2)}€
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
